"use strict";
/**
 * Polymarket Order Creation
 * Creates Polymarket order requests from app state
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.createPolymarketOrderRequest = createPolymarketOrderRequest;
const state_1 = require("../state");
const slippage_utils_1 = require("./slippage-utils");
const permissions_1 = require("../permissions");
/**
 * Create Polymarket order request from app state
 * Handles three scenarios:
 * 1. Regular buy (no position) - market buy with slippage
 * 2. Position flip (has opposite position) - batch sell opposite + buy new
 * 3. Regular sell (has position) - market sell of current position
 *
 * Supports Manual Price Mode: when active, sends explicit limit price
 *
 * @param marketId - Market slug/id
 * @param side - yes or no
 * @param action - buy or sell
 * @returns Polymarket order request object
 */
function createPolymarketOrderRequest(marketId, side, action = "buy") {
    const appState = (0, state_1.getState)();
    const currentMarket = appState.selectedMarket;
    if (!currentMarket) {
        throw new Error("No market selected");
    }
    // Get token IDs from market data
    const clobTokenIds = currentMarket.clobTokenIds;
    if (!clobTokenIds || clobTokenIds.length !== 2) {
        throw new Error("Market missing clobTokenIds for Polymarket trading");
    }
    // Token IDs: [yesTokenId, noTokenId]
    const yesTokenId = clobTokenIds[0];
    const noTokenId = clobTokenIds[1];
    const targetTokenId = side === "yes" ? yesTokenId : noTokenId;
    const oppositeTokenId = side === "yes" ? noTokenId : yesTokenId;
    // Get trading settings
    const dollarAmount = appState.tradingSettings.dollarAmount;
    // Owner-gated (see permissions.ts); force off when the tier can't use it.
    const closeOppositeOnBuy = appState.tradingSettings.closeOppositeOnBuy !== false &&
        (0, permissions_1.permsAllowCloseOpposite)(appState.permissions);
    const slippagePercent = (0, slippage_utils_1.getEffectiveSlippage)(appState.tradingSettings, side);
    // Manual price mode: convert integer cents (1-99) to decimal (0.01-0.99)
    const isManualMode = appState.tradingSettings.manualPriceMode === true;
    if (isManualMode) {
        const manualPriceSelected = side === "yes"
            ? appState.tradingSettings.manualYesPriceSelected === true
            : appState.tradingSettings.manualNoPriceSelected === true;
        if (!manualPriceSelected) {
            throw new Error(`Select a manual ${side.toUpperCase()} price before placing an order`);
        }
    }
    const manualPrice = isManualMode
        ? (side === "yes"
            ? appState.tradingSettings.manualYesPrice
            : appState.tradingSettings.manualNoPrice) / 100
        : undefined;
    // Get current position
    const position = appState.currentPosition;
    const yesQuantity = position?.yes_quantity || 0;
    const noQuantity = position?.no_quantity || 0;
    const oppositeQuantity = side === "yes" ? noQuantity : yesQuantity;
    // ============================================================================
    // Scenario 1: SELL order - sell current position
    // ============================================================================
    if (action === "sell") {
        const currentQuantity = side === "yes" ? yesQuantity : noQuantity;
        if (currentQuantity <= 0) {
            throw new Error(`No ${side.toUpperCase()} position to sell`);
        }
        // Partial sell: cap to fixedSellQuantity
        let sellSize = currentQuantity;
        if (appState.tradingSettings.sellMode === "partial" &&
            appState.tradingSettings.fixedSellQuantity > 0) {
            sellSize = Math.min(currentQuantity, appState.tradingSettings.fixedSellQuantity);
        }
        return {
            token_id: targetTokenId,
            side: "SELL",
            size: sellSize,
            order_type: "market",
        };
    }
    // ============================================================================
    // Scenario 2: BUY with opposite position - position flip (batch order)
    // ============================================================================
    if (closeOppositeOnBuy && oppositeQuantity > 0) {
        return {
            token_id: targetTokenId,
            side: "BUY",
            dollar_amount: dollarAmount,
            order_type: "limit",
            slippage_percent: isManualMode ? 0 : slippagePercent,
            close_opposite: true,
            opposite_token_id: oppositeTokenId,
            opposite_size: oppositeQuantity,
            ...(manualPrice !== undefined ? { price: manualPrice } : {}),
        };
    }
    // ============================================================================
    // Scenario 3: Regular BUY - no position or adding to existing position
    // ============================================================================
    return {
        token_id: targetTokenId,
        side: "BUY",
        dollar_amount: dollarAmount,
        order_type: "limit",
        slippage_percent: isManualMode ? 0 : slippagePercent,
        ...(manualPrice !== undefined ? { price: manualPrice } : {}),
    };
}
